import * as React from 'react'
import { TouchableOpacity, StyleSheet, ViewStyle, View } from 'react-native'
import { Text } from './Text'
import { colors } from './theme'
import { useBreakpoints, ResponsiveValue } from './hooks/useBreakpoints'
import { spacing } from './types'

type Size = 'sm' | 'md' | 'lg'

interface SelectableButtonProps {
	label: string
	selected?: boolean
	onPress?: () => void
	size?: Size | ResponsiveValue<Size>
	disabled?: boolean
	style?: ViewStyle
	description?: string
}

const sizeStyles = {
	sm: {
		paddingVertical: spacing.xs,
		paddingHorizontal: spacing.sm,
		textLevel: 'small' as const,
		descriptionLevel: 'xsmall' as const,
	},
	md: {
		paddingVertical: spacing.sm,
		paddingHorizontal: spacing.md,
		textLevel: 'body' as const,
		descriptionLevel: 'small' as const,
	},
	lg: {
		paddingVertical: spacing.sm,
		paddingHorizontal: spacing.lg,
		textLevel: 'large' as const,
		descriptionLevel: 'body' as const,
	},
}

export function SelectableButton({
	label,
	selected = false,
	onPress,
	size = 'md',
	disabled = false,
	style,
	description,
}: SelectableButtonProps) {
	const { value } = useBreakpoints()
	const sizeValue = typeof size === 'object' ? value(size) : size

	const sizeStyle = sizeStyles[sizeValue]

	return (
		<TouchableOpacity
			onPress={disabled ? undefined : onPress}
			style={[
				styles.button,
				{
					backgroundColor: selected ? global.orgColor : colors.white,
					borderColor: selected ? global.orgColor : colors.gray500,
					paddingVertical: sizeStyle.paddingVertical,
					paddingHorizontal: sizeStyle.paddingHorizontal,
					opacity: disabled ? 0.5 : 1,
				},
				style,
			]}
			activeOpacity={0.8}
			disabled={disabled}
			accessibilityRole="button"
			accessibilityState={{ selected, disabled }}
			accessibilityLabel={`${label}${description ? `, ${description}` : ''}`}
		>
			<View style={styles.content}>
				<Text
					level={sizeStyle.textLevel}
					weight={selected ? 'strong' : 'regular'}
					style={{
						color: selected ? colors.white : colors.gray900,
						textAlign: 'center',
					}}
					numberOfLines={2}
				>
					{label}
				</Text>
				{description && (
					<Text
						level={sizeStyle.descriptionLevel}
						weight="regular"
						style={{
							color: selected ? colors.white : colors.gray600,
							marginTop: spacing.xs,
							textAlign: 'center',
						}}
						numberOfLines={2}
					>
						{description}
					</Text>
				)}
			</View>
		</TouchableOpacity>
	)
}

const styles = StyleSheet.create({
	button: {
		borderWidth: 1,
		borderRadius: 8,
		alignItems: 'center',
		justifyContent: 'center',
		height: '100%',
	},
	content: {
		alignItems: 'center',
		justifyContent: 'center',
	},
})
